/* eslint-disable no-param-reassign */
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { enqueueInfoSnackbar, enqueueErrorSnackbar } from 'actions/Notifications';
import { getLocalizedMessage } from 'util/useLocalizedNotifications';
import _ from 'lodash';

// ---------------------------------------------------------------------------
// Thunks
// ---------------------------------------------------------------------------

export const loadFacetValues = createAsyncThunk(
  'facetValues/loadAsync',
  async ({ shopId, facetId }, { extra, dispatch, rejectWithValue }) => {
    try {
      const { facetValuesApi } = extra.serviceRegistry;
      if (!facetValuesApi) {
        return rejectWithValue('facetValuesApi not available on current transport');
      }
      const result = await facetValuesApi.load(shopId, facetId);
      return result;
    } catch (err) {
      dispatch(enqueueErrorSnackbar(
        err.message || getLocalizedMessage('notifications.error.fetchingError', { entity: 'facet values' }),
        { variant: 'error' },
      ));
      return rejectWithValue(err?.message || 'load failed');
    }
  },
);

export const saveFacetValues = createAsyncThunk(
  'facetValues/saveAsync',
  async ({ shopId, facetId }, { extra, getState, dispatch, rejectWithValue }) => {
    try {
      const { facetValuesApi } = extra.serviceRegistry;
      if (!facetValuesApi) {
        return rejectWithValue('facetValuesApi not available on current transport');
      }
      const currentValues = getState().facetValues.currentValues;
      const mappings = currentValues.map((v) => ({
        rawValue: v.rawValue,
        mappedValue: v.mappedValue,
      }));
      const result = await facetValuesApi.save(shopId, facetId, mappings);
      dispatch(enqueueInfoSnackbar(
        getLocalizedMessage('notifications.success.savedSuccessfully', { entity: 'value mappings' }),
        { variant: 'success' },
      ));
      return result;
    } catch (err) {
      dispatch(enqueueErrorSnackbar(
        err.message || getLocalizedMessage('notifications.error.savingError', { entity: 'facet values' }),
        { variant: 'error' },
      ));
      return rejectWithValue(err?.message || 'save failed');
    }
  },
);

// ---------------------------------------------------------------------------
// Initial state
// ---------------------------------------------------------------------------

const initialState = {
  facetId: null,
  facetType: null,
  originalValues: [],
  currentValues: [],
  isLoading: false,
  isSaving: false,
  error: null,
  isOpen: false,
};

// ---------------------------------------------------------------------------
// Slice
// ---------------------------------------------------------------------------

const slice = createSlice({
  name: 'facetValues',
  initialState,
  reducers: {
    openEditor: (state, { payload }) => {
      state.facetId = payload.facetId;
      state.facetType = payload.facetType;
      state.isOpen = true;
      state.originalValues = [];
      state.currentValues = [];
      state.error = null;
    },
    closeEditor: (state) => {
      state.isOpen = false;
      state.facetId = null;
      state.facetType = null;
      state.originalValues = [];
      state.currentValues = [];
      state.error = null;
    },
    updateMapping: (state, { payload }) => {
      const { rawValue, mappedValue } = payload;
      const idx = state.currentValues.findIndex((v) => v.rawValue === rawValue);
      if (idx >= 0) {
        state.currentValues[idx] = { ...state.currentValues[idx], mappedValue };
      }
    },
    discardChanges: (state) => {
      state.currentValues = _.cloneDeep(state.originalValues);
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(loadFacetValues.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(loadFacetValues.fulfilled, (state, { payload }) => {
        state.facetId = payload.facetId;
        state.facetType = payload.facetType;
        state.originalValues = payload.values;
        state.currentValues = _.cloneDeep(payload.values);
        state.isLoading = false;
      })
      .addCase(loadFacetValues.rejected, (state, { payload }) => {
        state.isLoading = false;
        state.error = payload || 'load failed';
      })
      .addCase(saveFacetValues.pending, (state) => {
        state.isSaving = true;
        state.error = null;
      })
      .addCase(saveFacetValues.fulfilled, (state, { payload }) => {
        state.originalValues = payload.values;
        state.currentValues = _.cloneDeep(payload.values);
        state.isSaving = false;
      })
      .addCase(saveFacetValues.rejected, (state, { payload }) => {
        state.isSaving = false;
        state.error = payload || 'save failed';
      });
  },
});

export const { actions } = slice;
export default slice.reducer;

// ---------------------------------------------------------------------------
// Selectors
// ---------------------------------------------------------------------------

export const selectIsOpen = (state) => state.facetValues.isOpen;
export const selectFacetId = (state) => state.facetValues.facetId;
export const selectFacetType = (state) => state.facetValues.facetType;
export const selectCurrentValues = (state) => state.facetValues.currentValues;
export const selectIsLoading = (state) => state.facetValues.isLoading;
export const selectIsSaving = (state) => state.facetValues.isSaving;
export const selectHasChanges = (state) =>
  !_.isEqual(state.facetValues.currentValues, state.facetValues.originalValues);
